/**
 * <casa-limited-sale>
 * ===================
 * Category-filtered, sortable collection page (Private Sale).
 *
 *  - Prefetches remaining paginated pages via the Section Rendering API so
 *    both filtering and sorting span the whole collection.
 *  - Filters product cards by product type (case-insensitive) via the
 *    `data-product-type` attribute stamped server-side.
 *  - Sorts by discount %, price, or title using `data-discount` /
 *    `data-price` / `data-title`.
 *  - Top horizontal bar and the collapsible side filter share state.
 *
 * Load-shift avoidance: the host carries `data-loading` until the prefetch
 * and the first sort+filter pass complete; the grid's children are
 * `visibility:hidden` until then, so the late-appended cards never cause a
 * visible reflow. The grid reserves space with a min-height.
 */
class CasaLimitedSale extends HTMLElement {
  constructor() {
    super();
    this.grid = null;
    this.statusEl = null;
    this.emptyEl = null;
    this.catButtons = [];
    this.sideToggles = [];
    this.sideFilter = null;
    this.sortSelect = null;
    this.activeFilterEl = null;
    this.colPicker = null;
    this.colLabel = null;
    this.fetched = false;
    this.totalPages = 1;
    this.currentPage = 1;
    this.sectionId = '';
    this.collectionUrl = '';
    this.activeTypes = []; // [] = All
    this.sortValue = 'discount-high-low';
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.statusEl = this.querySelector('[data-status-text]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.barButtons = Array.from(this.querySelectorAll('.casa-cat-filter--bar [data-cat-button]'));
    this.sideButtons = Array.from(this.querySelectorAll('.casa-cat-filter--side [data-cat-button]'));
    this.catButtons = [...this.barButtons, ...this.sideButtons];
    this.sideToggles = Array.from(this.querySelectorAll('[data-side-toggle]'));
    this.sideFilter = this.querySelector('[data-side-filter]');
    this.sortSelect = this.querySelector('[data-sort-select]');
    this.activeFilterEl = this.querySelector('[data-active-filter]');
    this.colPicker = this.querySelector('[data-col-picker]');
    this.colLabel = this.querySelector('[data-col-label]');

    const pag = this.querySelector('[data-paginate]');
    this.totalPages = parseInt(pag?.dataset.totalPages || '1', 10);
    this.currentPage = parseInt(pag?.dataset.currentPage || '1', 10);
    this.sectionId = pag?.dataset.sectionId || this.dataset.sectionId || '';
    this.collectionUrl = this.dataset.collectionUrl || window.location.pathname;
    this.sortValue = this.dataset.defaultSort || 'discount-high-low';

    // Column controls — restore saved preference (shared key with casa-column-controls)
    try {
      const saved = localStorage.getItem('casa-grid-columns');
      if (saved) this.#applyColumns(saved, false);
    } catch (e) {}

    this.#hydrateFromUrl();

    // Sort + filter the server-rendered first page right away so the
    // visible page is correct from the first paint.
    this.#applySortAndFilter();

    // Failsafe: never leave deferred cards hidden if prefetch hangs.
    this.revealTimer = setTimeout(() => this.#reveal(), 8000);

    this.catButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.#onCategoryClick(btn));
    });
    this.sideToggles.forEach((btn) => btn.addEventListener('click', () => this.#toggleSide()));
    this.sortSelect?.addEventListener('change', () => {
      this.sortValue = this.sortSelect.value;
      this.#syncUrl();
      this.#applySortAndFilter();
    });
    this.querySelectorAll('.casa-limited-sale__col-input').forEach((inp) => {
      inp.addEventListener('change', () => this.#applyColumns(inp.value));
    });

    // Prefetch first, then reveal — keeps the page from shifting on load.
    this.#prefetchRemainingPages();
  }

  #hydrateFromUrl() {
    const url = new URL(window.location.href);
    const cat = url.searchParams.get('category');
    if (cat) {
      this.activeTypes = cat
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
    }
    const sort = url.searchParams.get('sort_by');
    if (sort && this.sortSelect?.querySelector(`option[value="${sort}"]`)) {
      this.sortValue = sort;
    }
    if (this.sortSelect) this.sortSelect.value = this.sortValue;
    this.#syncButtonStates();
    this.#syncActiveFilter();
  }

  #syncUrl() {
    const url = new URL(window.location.href);
    if (this.activeTypes.length) {
      url.searchParams.set('category', this.activeTypes.join(','));
    } else {
      url.searchParams.delete('category');
    }
    url.searchParams.set('sort_by', this.sortValue);
    window.history.replaceState({}, '', url.toString());
  }

  /** Parse the comma-separated types from a button's data-cat-types attribute. */
  #btnTypes(btn) {
    const raw = (btn.dataset.catTypes || '').trim();
    return raw ? raw.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean) : [];
  }

  #onCategoryClick(btn) {
    const types = this.#btnTypes(btn);
    const isBarButton = this.barButtons.includes(btn);

    if (types.length === 0) {
      // "All Products" — always clears the active set.
      this.activeTypes = [];
    } else if (isBarButton) {
      // Bar is single-select: replace active types with only this button's types.
      this.activeTypes = [...types];
    } else {
      // Sidebar is multi-select: toggle this button's type-set in/out as a unit.
      const active = new Set(this.activeTypes);
      const allActive = types.every((t) => active.has(t));
      if (allActive) {
        types.forEach((t) => active.delete(t));
      } else {
        types.forEach((t) => active.add(t));
      }
      this.activeTypes = Array.from(active);
    }

    this.#syncButtonStates();
    this.#syncActiveFilter();
    this.#syncUrl();
    this.#applySortAndFilter();
    setTimeout(() => {
      this.querySelector('.casa-limited-sale__main')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 500);
  }

  /** Mark each button active.
   *  Bar buttons (single-select): active only when their types are the exact active set.
   *  Sidebar buttons (multi-select): active when all of their types are in the active set. */
  #syncButtonStates() {
    const active = new Set(this.activeTypes);
    this.barButtons.forEach((btn) => {
      const types = this.#btnTypes(btn);
      const isActive = types.length === 0
        ? active.size === 0
        : types.length === this.activeTypes.length && types.every((t) => active.has(t));
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    this.sideButtons.forEach((btn) => {
      const types = this.#btnTypes(btn);
      const isActive = types.length === 0
        ? active.size === 0
        : types.every((t) => active.has(t));
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  /** Expand / collapse the side filter column entirely (it collapses to
   *  zero horizontal space when closed; the grid spans the full row). */
  #toggleSide() {
    if (!this.sideFilter) return;
    const willOpen = this.sideFilter.hasAttribute('hidden');
    this.sideFilter.toggleAttribute('hidden', !willOpen);
    this.sideToggles.forEach((btn) => {
      btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });
  }

  /** Update the H2 above the grid with the active category name(s).
   *  Uses "Multiple Categories" when more than one sidebar category is active. */
  #syncActiveFilter() {
    if (!this.activeFilterEl) return;
    if (this.activeTypes.length === 0) {
      this.activeFilterEl.textContent = '';
      return;
    }
    const activeSideCount = this.sideButtons.filter((b) => {
      const types = this.#btnTypes(b);
      return types.length > 0 && types.every((t) => this.activeTypes.includes(t));
    }).length;

    if (activeSideCount > 1) {
      this.activeFilterEl.textContent = 'Multiple Categories';
      return;
    }

    const labels = [];
    const seen = new Set();
    this.sideButtons.forEach((b) => {
      const types = this.#btnTypes(b);
      if (types.length === 0) return;
      const key = types.slice().sort().join('|');
      if (seen.has(key)) return;
      const isFullyActive = types.every((t) => this.activeTypes.includes(t));
      if (!isFullyActive) return;
      const label = b.querySelector('.casa-cat-filter__side-label')?.textContent.trim();
      if (label) {
        labels.push(label);
        seen.add(key);
      }
    });
    this.activeFilterEl.textContent = labels.join(' + ');
  }

  /** Apply column count: updates data attr, label, radio, and closes the dropdown. */
  #applyColumns(n, persist = true) {
    this.dataset.columns = n;
    if (this.colLabel) this.colLabel.textContent = `${n} columns`;
    const input = this.querySelector(`.casa-limited-sale__col-input[value="${n}"]`);
    if (input) input.checked = true;
    if (this.colPicker) this.colPicker.open = false;
    if (persist) {
      try { localStorage.setItem('casa-grid-columns', n); } catch (e) {}
    }
  }

  async #prefetchRemainingPages() {
    if (this.fetched || this.totalPages <= 1) {
      // Nothing to fetch — the server page is the whole collection.
      this.#reveal();
      return;
    }
    this.fetched = true;

    const baseUrl = new URL(window.location.href);
    const fetches = [];
    for (let p = this.currentPage + 1; p <= this.totalPages; p++) {
      const pageUrl = new URL(this.collectionUrl, window.location.origin);
      baseUrl.searchParams.forEach((v, k) => {
        if (k !== 'page' && k !== 'section_id' && k !== 'category' && k !== 'sort_by') {
          pageUrl.searchParams.set(k, v);
        }
      });
      pageUrl.searchParams.set('page', String(p));
      pageUrl.searchParams.set('section_id', this.sectionId);
      fetches.push(this.#fetchPage(pageUrl.toString()));
    }

    try {
      const chunks = await Promise.all(fetches);
      // Dedupe by product+variant: each on-sale color renders its own tile, so
      // multiple cards share a product id. Keying on variant id keeps every
      // distinct color tile while still dropping true duplicates that straddle
      // a pagination boundary.
      const cardKey = (el) => `${el.dataset.productId}:${el.dataset.variantId || ''}`;
      const existing = new Set(
        Array.from(this.grid.querySelectorAll('[data-card]')).map(cardKey)
      );
      const frag = document.createDocumentFragment();
      for (const html of chunks) {
        if (!html) continue;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('[data-card]').forEach((card) => {
          const key = cardKey(card);
          if (!existing.has(key)) {
            existing.add(key);
            // Deferred = display:none until reveal, so the append doesn't
            // reflow the page while the user is looking at page one.
            card.setAttribute('data-deferred', '');
            frag.appendChild(card);
          }
        });
      }
      this.grid.appendChild(frag);
    } catch (err) {
      console.error('[casa-limited-sale] prefetch failed', err);
    } finally {
      this.#applySortAndFilter();
      this.#reveal();
    }
  }

  /** Drop the loading state — fades the grid in once content is settled.
   *  Deferred to the next frame so the filtered/sorted layout is painted
   *  before the opacity transition starts (no flash of unfiltered content). */
  #reveal() {
    if (this.revealed) return;
    this.revealed = true;
    clearTimeout(this.revealTimer);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.removeAttribute('data-loading'));
    });
  }

  async #fetchPage(url) {
    try {
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) return '';
      return await res.text();
    } catch (e) {
      console.error('[casa-limited-sale] fetch error', e);
      return '';
    }
  }

  #applySortAndFilter() {
    if (!this.grid) return;
    const cards = Array.from(this.grid.querySelectorAll('[data-card]'));
    const active = this.activeTypes;

    // Filter
    let visible = 0;
    for (const card of cards) {
      const type = (card.dataset.productType || '').trim().toLowerCase();
      const show = active.length === 0 || active.includes(type);
      card.hidden = !show;
      if (show) visible += 1;
    }

    // Sort (operates on all cards; hidden ones sort harmlessly too)
    const sorted = cards.slice().sort((a, b) => this.#compare(a, b));
    const frag = document.createDocumentFragment();
    sorted.forEach((card) => frag.appendChild(card));
    this.grid.appendChild(frag);

    if (this.emptyEl) this.emptyEl.hidden = visible !== 0;
    this.#setStatus(visible);
  }

  #compare(a, b) {
    const num = (el, key) => parseFloat(el.dataset[key] || '0') || 0;
    const str = (el, key) => (el.dataset[key] || '').toLowerCase();
    switch (this.sortValue) {
      case 'discount-high-low':
        return num(b, 'discount') - num(a, 'discount');
      case 'discount-low-high':
        return num(a, 'discount') - num(b, 'discount');
      case 'price-low-high':
        return num(a, 'price') - num(b, 'price');
      case 'price-high-low':
        return num(b, 'price') - num(a, 'price');
      case 'title-a-z':
        return str(a, 'title').localeCompare(str(b, 'title'));
      case 'title-z-a':
        return str(b, 'title').localeCompare(str(a, 'title'));
      default:
        return 0;
    }
  }

  #setStatus(count) {
    if (!this.statusEl) return;
    this.statusEl.textContent = count === 1 ? '1 product' : `${count} products`;
  }
}

if (!customElements.get('casa-limited-sale')) {
  customElements.define('casa-limited-sale', CasaLimitedSale);
}
